import { useFragment } from "@/generated";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";
import {
  DeleteProjectsDocument,
  LessonFieldsFragmentDoc,
  type LinkedCopy,
  ProjectFieldsFragmentDoc,
  SlideFieldsFragmentDoc,
} from "../../../generated/graphql";
import { updateDeletedLessonsInCache } from "../lessons/useDeleteLessons";
import { updateDeletedSlidesInCache } from "../slides/useDeleteSlides";

/**
 * Hook for soft deleting projects recursively.
 *
 * Marks projects as deleted (isDeleted = true) and handles all children recursively:
 * - Child projects: removes parent ID from their parentId, recursively deletes if parentId becomes empty
 * - Child lessons: removes parent ID from their parentId, marks deleted if parentId becomes empty
 * - Lesson slides: marks all slides as deleted when their lesson is deleted
 *
 * Note: isTrash state is left unchanged, only isDeleted is toggled.
 *
 * @returns An object containing:
 *   - deleteProjects: Mutation function to soft delete projects (accepts single ID or array of IDs)
 *   - loading: Boolean indicating if the mutation is in progress
 *   - error: ApolloError if the mutation failed, undefined otherwise
 *
 * @example
 * const { deleteProjects, loading, error } = useDeleteProjects();
 * // Delete single project
 * await deleteProjects(["project123"]);
 * // Delete multiple projects
 * await deleteProjects(["project1", "project2", "project3"]);
 */
export function useDeleteProjects() {
  const [deleteProjectsMutation, { loading, error }] = useMutation(DeleteProjectsDocument, {
    update(cache, { data }) {
      if (!data?.deleteProjects) return;

      const lessons = data?.deleteProjects.lessons.map((lesson) =>
        useFragment(LessonFieldsFragmentDoc, lesson)
      );

      updateDeletedLessonsInCache(cache, lessons);

      const slides = data?.deleteProjects.slides.map((slide) =>
        useFragment(SlideFieldsFragmentDoc, slide)
      );

      updateDeletedSlidesInCache(cache, slides);

      const deletedProjects = data.deleteProjects.projects.map((project) =>
        useFragment(ProjectFieldsFragmentDoc, project)
      );

      const parentId = deletedProjects[0]?.parentId;

      try {
        cache.batch({
          update(cache) {
            for (const project of deletedProjects) {
              if (!project.isDeleted) return;

              const id = cache.identify(project);

              if (id) {
                cache.evict({ id });
              }

              // Remove from original item's linkedCopies if this is a linked copy
              if (project.isLinkedCopy) {
                const linkedId = cache.identify({ __typename: "LinkedCopy", id: project.id });

                if (linkedId) {
                  cache.evict({ id: linkedId });
                }
              }
            }

            if (parentId === null) {
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  rootTrashCount(existingData) {
                    return existingData - deletedProjects.filter((p) => p.isDeleted).length;
                  },
                },
              });
            } else {
              cache.modify({
                id: `Project:${parentId}`,
                fields: {
                  trashCount(existingData = 0) {
                    return existingData - deletedProjects.filter((p) => p.isDeleted).length;
                  },
                },
              });
            }
          },
        });

        cache.gc();
      } catch (error) {
        console.error("Delete projects cache update failed:", error);
      }
    },
  });

  const deleteProjects = async (ids: string | string[], skipCheck: boolean) => {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    return withToast(
      async () => {
        const result = await deleteProjectsMutation({ variables: { ids: idsArray, skipCheck } });

        const maskedProjects = result.data?.deleteProjects.projects ?? [];
        const deletedProjects = maskedProjects.map((project) =>
          useFragment(ProjectFieldsFragmentDoc, project)
        );

        return deletedProjects;
      },
      {
        loading: `Deleting ${idsArray.length} project${idsArray.length !== 1 ? "s" : ""}...`,
        success: `Successfully deleted ${idsArray.length} project${idsArray.length !== 1 ? "s" : ""}`,
        error: "Failed to delete projects. Please try again.",
      },
      "deleteProjects"
    );
  };

  return { deleteProjects, loading, error };
}
